import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import GppBadIcon from '@mui/icons-material/GppBad';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

/** A single identity-verification entry from ResourceData.verifications[]. */
export interface VerificationEntry {
  field: string;
  status?: 'pending' | 'done';
  outcome?: 'true' | 'false' | 'other';
  confidence?: number | null;
  message?: string | null;
  provider?: string;
}

/**
 * Given a resource's verifications[] and a field name, pick the latest entry
 * for that field (last in the array).
 */
export function latestVerificationFor(
  verifications: VerificationEntry[] | undefined | null,
  fieldName: string,
): VerificationEntry | undefined {
  if (!Array.isArray(verifications)) return undefined;
  let found: VerificationEntry | undefined;
  for (const v of verifications) {
    if (v?.field === fieldName) found = v;
  }
  return found;
}

/**
 * Small status chip for an image field's identity-verification result.
 * Renders nothing when there is no verification for the field.
 */
const VerificationChip: React.FC<{ verification?: VerificationEntry | null }> = ({ verification }) => {
  if (!verification) return null;

  const { status, outcome, confidence, message } = verification;

  let color: 'warning' | 'success' | 'error' | 'default' = 'default';
  let icon = <HelpOutlineIcon />;
  let label = 'Inconclusive';

  if (status === 'pending') {
    color = 'warning';
    icon = <HourglassTopIcon />;
    label = 'Verifying…';
  } else if (outcome === 'true') {
    color = 'success';
    icon = <VerifiedIcon />;
    label = confidence != null ? `Verified (${confidence}%)` : 'Verified';
  } else if (outcome === 'false') {
    color = 'error';
    icon = <GppBadIcon />;
    label = 'Not verified';
  }

  const chip = (
    <Chip size="small" color={color} icon={icon} label={label} variant="outlined" />
  );

  return message ? <Tooltip title={message}>{chip}</Tooltip> : chip;
};

export default VerificationChip;
